import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import axios from "axios";
import { Hourglass, ChevronDown, ChevronRight, BadgePercent, Tag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DepartmentNav — surfaces all visible departments (live + coming_soon).
 *
 * Two layouts:
 *   - <DepartmentNav variant="strip" />     horizontal scrollable strip (mobile top)
 *   - <DepartmentNav variant="sidebar" />   accordion-style vertical list (desktop sidebar + mobile drawer)
 *
 * Sidebar variant: each dept is expandable to show its categories nested. The
 * dept currently being viewed via /d/<slug> or /c/<cat> auto-expands.
 */
export default function DepartmentNav({ variant = "strip" }) {
    const [data, setData] = useState([]);
    const [open, setOpen] = useState(null); // currently expanded slug
    const loc = useLocation();

    useEffect(() => {
        let alive = true;
        Promise.all([
            axios.get(`${API}/departments`),
            axios.get(`${API}/categories?storefront=true`),
        ]).then(([d, c]) => {
            if (!alive) return;
            const cats = c.data || [];
            const byDept = new Map();
            for (const cat of cats) {
                const slug = cat.department_slug || "clothing";
                if (!byDept.has(slug)) byDept.set(slug, []);
                byDept.get(slug).push(cat);
            }
            const enriched = (d.data || []).map(dept => ({
                ...dept,
                categories: byDept.get(dept.slug) || [],
            }));
            setData(enriched);
            // Auto-expand whichever dept matches the current URL
            const match = loc.pathname.match(/^\/d\/([^/]+)/);
            if (match) {
                setOpen(match[1]);
            } else if (loc.pathname.startsWith("/c/")) {
                // Find which dept the category belongs to
                const catName = decodeURIComponent(loc.pathname.replace("/c/", ""));
                const owner = enriched.find(d2 => d2.categories.some(c2 => c2.name === catName));
                if (owner) setOpen(owner.slug);
            } else if (enriched.length > 0) {
                // Default: open the first live department
                const firstLive = enriched.find(d2 => d2.status === "live");
                setOpen(firstLive ? firstLive.slug : enriched[0].slug);
            }
        }).catch(() => {});
        return () => { alive = false; };
    }, [loc.pathname]);

    if (data.length === 0) {
        // Skeleton placeholder — keeps layout stable so the sidebar doesn't reflow once data loads
        if (variant === "sidebar") {
            return (
                <div className="mb-6" data-testid="dept-nav-sidebar-skeleton">
                    <p className="overline mb-3">Browse collections</p>
                    <div className="space-y-2 animate-pulse">
                        {[1, 2, 3, 4, 5].map(i => (
                            <div key={i} className="h-9 rounded-md bg-stone-100" />
                        ))}
                    </div>
                </div>
            );
        }
        return (
            <div className="lg:hidden border-b border-rosegold/15 bg-ivory">
                <div className="max-w-[90rem] mx-auto px-4 sm:px-6 py-2.5">
                    <div className="flex gap-2">
                        {[1,2,3,4,5].map(i => <div key={i} className="h-7 w-20 rounded-full bg-stone-100 animate-pulse" />)}
                    </div>
                </div>
            </div>
        );
    }

    if (variant === "strip") {
        return (
            <div className="lg:hidden border-b border-rosegold/15 bg-ivory" data-testid="dept-nav-strip">
                <div className="max-w-[90rem] mx-auto px-4 sm:px-6 overflow-x-auto py-2.5 scrollbar-hide">
                    <nav className="flex items-center gap-1 min-w-max">
                        {data.map((d) => (
                            <NavLink
                                key={d.slug}
                                to={`/d/${d.slug}`}
                                data-testid={`dept-strip-${d.slug}`}
                                className={({ isActive }) => `inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider font-semibold whitespace-nowrap rounded-full transition-colors ${
                                    isActive
                                        ? "bg-ink text-ivory"
                                        : d.status === "coming_soon"
                                            ? "text-ink-soft hover:bg-rosegold/10"
                                            : "text-ink hover:bg-rosegold/10"
                                }`}
                            >
                                {d.name}
                                {d.status === "coming_soon" && (
                                    <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-wine text-ivory font-bold tracking-wider">
                                        <Hourglass size={8} /> Soon
                                    </span>
                                )}
                            </NavLink>
                        ))}
                    </nav>
                </div>
            </div>
        );
    }

    // sidebar accordion variant
    return (
        <div className="mb-6" data-testid="dept-nav-sidebar">
            <p className="overline mb-3">Browse collections</p>
            <nav className="space-y-1">
                {data.map((d) => (
                    <DeptItem
                        key={d.slug}
                        dept={d}
                        isOpen={open === d.slug}
                        onToggle={() => setOpen(open === d.slug ? null : d.slug)}
                    />
                ))}
            </nav>
            <div className="mt-3 pt-3 border-t border-stone-200 space-y-0.5">
                <NavLink
                    to="/offers"
                    onClick={() => { try { sessionStorage.setItem("jns_offers_return_to", window.location.pathname + window.location.search); } catch { /* noop */ } }}
                    data-testid="dept-nav-offers"
                    className={({ isActive: a }) => `flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${a ? "bg-ivory-soft text-rosegold font-bold" : "text-ink hover:bg-ivory-soft hover:text-rosegold"}`}
                >
                    <BadgePercent size={14} /> Offers &amp; promo codes
                </NavLink>
                <NavLink
                    to="/sale"
                    data-testid="dept-nav-sale"
                    className={({ isActive: a }) => `flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${a ? "bg-ivory-soft text-wine font-bold" : "text-ink hover:bg-ivory-soft hover:text-wine"}`}
                >
                    <Tag size={14} /> Sale
                </NavLink>
            </div>
        </div>
    );
}

function DeptItem({ dept, isOpen, onToggle }) {
    // Parent row is just a toggle — active highlight belongs to the sub-link (All X / categories)
    // to avoid double-highlighting both rows simultaneously.
    return (
        <div data-testid={`dept-nav-item-${dept.slug}`}>
            <button
                type="button"
                onClick={onToggle}
                data-testid={`dept-link-${dept.slug}`}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors text-ink hover:bg-ivory-soft ${isOpen ? "font-semibold" : ""}`}
            >
                <span className="inline-flex items-center gap-1.5 truncate">
                    <span className="truncate">{dept.name}</span>
                    {dept.status === "coming_soon" && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-wine text-ivory font-bold tracking-wider flex-shrink-0">
                            <Hourglass size={8} /> Soon
                        </span>
                    )}
                </span>
                {dept.categories.length > 0 ? (
                    isOpen ? <ChevronDown size={13} className="text-ink-soft flex-shrink-0" /> : <ChevronRight size={13} className="text-ink-soft flex-shrink-0" />
                ) : null}
            </button>
            {isOpen && (
                <div className="ml-3 pl-3 mt-0.5 border-l border-rosegold/20 space-y-0.5" data-testid={`dept-cats-${dept.slug}`}>
                    <NavLink
                        to={`/d/${dept.slug}`}
                        className={({ isActive: a }) => `flex items-center gap-1.5 px-2 py-1.5 text-[12px] rounded transition-colors ${a ? "bg-ivory-soft text-ink font-bold border-l-2 border-rosegold -ml-[14px] pl-[14px]" : "text-ink-soft hover:text-ink"}`}
                        data-testid={`dept-all-${dept.slug}`}
                    >
                        All {dept.name}
                    </NavLink>
                    {dept.categories.map((c) => (
                        <NavLink
                            key={c.id}
                            to={`/c/${encodeURIComponent(c.name)}`}
                            data-testid={`dept-nav-cat-${c.id}`}
                            className={({ isActive: a }) => `flex items-center gap-1.5 px-2 py-1.5 text-[12px] rounded transition-colors ${a ? "bg-ivory-soft text-ink font-bold border-l-2 border-rosegold -ml-[14px] pl-[14px]" : "text-ink-soft hover:text-ink"}`}
                        >
                            {c.name}
                        </NavLink>
                    ))}
                </div>
            )}
        </div>
    );
}
